import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded'
import { Box, FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material'

import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUAInsightsActions, useUAInsightsScope } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'

interface UAInsightsGenreSelectorProps {
  size?: 'small' | 'medium'
  label?: string
  sx?: object
  /** When true, the underlying FormControl is greyed-out and unclickable. */
  disabled?: boolean
}

const ALL_GENRES_VALUE = '__all__'

/**
 * Dashboard-wide genre filter for UA Insights. The selection is persisted to
 * the URL (`?genreFilter=<id>`) via the navigation context so every dashboard
 * hook (summary cards, prevalent / trending / sunsetting tags, listings)
 * recomputes against the same scope. The first option, "All Genres", clears
 * the filter.
 */
export const UAInsightsGenreSelector: FC<UAInsightsGenreSelectorProps> = ({ size = 'small', label, sx, disabled = false }) => {
  const { t } = useTranslation('ua-insights')
  const { genreFilter } = useUAInsightsScope()
  const { setGenreFilter } = useUAInsightsActions()

  const { genres } = useUAInsightsMeta()
  const { genreLabel } = useUATaxonomy()
  const effectiveLabel = label ?? t('genre_filter_label')

  const selectValue = genreFilter && genres.some((g) => g.id === genreFilter) ? genreFilter : ALL_GENRES_VALUE

  const handleChange = (event: SelectChangeEvent<string>) => {
    const next = event.target.value
    setGenreFilter(next === ALL_GENRES_VALUE ? null : next)
  }

  const renderLabel = (value: string) => {
    const genre = value === ALL_GENRES_VALUE ? undefined : genres.find((g) => g.id === value)
    return genre ? genreLabel(genre) : t('all_genres')
  }

  return (
    <FormControl size={size} disabled={disabled} sx={{ minWidth: 200, ...sx }}>
      <Select
        value={selectValue}
        onChange={handleChange}
        color="primary"
        inputProps={{ 'aria-label': effectiveLabel }}
        renderValue={(value) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CategoryRoundedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <span>{renderLabel(value)}</span>
          </Box>
        )}
        sx={{
          borderRadius: 999,
          backgroundColor: 'background.paper',
        }}
      >
        <MenuItem key={ALL_GENRES_VALUE} value={ALL_GENRES_VALUE}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CategoryRoundedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <span>{t('all_genres')}</span>
          </Box>
        </MenuItem>
        {genres.map((genre) => (
          <MenuItem key={genre.id} value={genre.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CategoryRoundedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <span>{genreLabel(genre)}</span>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
